import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User } from 'oidc-client-ts';
import { currentUser, login, logout, userManager } from './oidc';

export interface AuthState {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  /** Claims of interest to the whole platform. */
  tenantId: string | null;
  roles: string[];
  /**
   * Fine-grained grants from the `platform_permissions` claim.
   *
   * Separate from `roles`, which are the coarse realm roles. A console uses these to decide what
   * to OFFER; the server decides what to allow, and every endpoint behind one of these is
   * @PreAuthorize'd independently. Hiding a control the caller cannot use is a courtesy, never
   * the control itself.
   */
  permissions: string[];
}

const AuthContext = createContext<AuthState | null>(null);

interface RealmAccessClaim {
  roles?: string[];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    currentUser().then((u) => {
      if (!cancelled) {
        // Never clobber a user set by the userLoaded event with a stale null:
        // right after the login callback, this initial lookup can resolve late
        // and would otherwise flash a signed-out screen before re-settling.
        setUser((prev) => prev ?? u);
        setLoading(false);
      }
    });
    const onLoaded = (u: User) => setUser(u);
    const onUnloaded = () => setUser(null);
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    return () => {
      cancelled = true;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
    };
  }, []);

  const doLogin = useCallback(() => {
    void login();
  }, []);
  const doLogout = useCallback(() => {
    // Entering the end-session redirect: signoutRedirect clears the stored
    // user BEFORE navigating, which fires userUnloaded -> user=null. Without
    // this loading gate the route guards would immediately auto-login and the
    // signinRedirect would override the logout navigation (the SSO cookie is
    // still alive, so the user would be silently signed straight back in).
    setLoading(true);
    void logout();
  }, []);

  const value = useMemo<AuthState>(() => {
    // Roles and tenant ride the ACCESS token (that's what services authorize on);
    // the ID token only carries profile claims. Decode the access token payload.
    let accessClaims: Record<string, unknown> = {};
    if (user?.access_token) {
      try {
        const payload = user.access_token.split('.')[1];
        accessClaims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
          string,
          unknown
        >;
      } catch {
        accessClaims = {};
      }
    }
    const realmAccess = (accessClaims['realm_access'] as RealmAccessClaim | undefined) ?? {};
    return {
      user,
      loading,
      login: doLogin,
      logout: doLogout,
      tenantId: (accessClaims['tenant_id'] as string | undefined) ?? null,
      roles: realmAccess.roles ?? [],
      // The fine-grained grants, not the coarse roles. KeycloakJwtAuthenticationConverter reads
      // the same claim server-side and turns it into PERM_ authorities; the console needs it to
      // decide what to OFFER, and the server still decides what to allow.
      permissions: (accessClaims['platform_permissions'] as string[] | undefined) ?? [],
    };
  }, [user, loading, doLogin, doLogout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
